import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { Avatar } from '@abyss/web/ui/Avatar';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Link } from '@abyss/web/ui/Link';
import { Text } from '@abyss/web/ui/Text';
import { Tooltip } from '@abyss/web/ui/Tooltip';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { BreadcrumbSessionStorage } from '../../models/BreadcrumbSessionStorage';
import { ProviderType } from '../../models/ProviderDetails';
import { useCareTeamStore } from '../../store/useCareTeamStore';
import { CareTeamState } from '../../store/useCareTeamStore/careTeamStore';
import { getSpecialityTitle } from '../../utils/card.utils';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import { getSelectedLocationIndex } from '../../utils/providerDetails.utils';
import {
  getNameAttrs,
  parseProviderName,
} from '../../utils/providerNames.utils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { CareTeamProviderSpecialtyContainer } from '../CareTeamCard/CareTeamCard.styled';
import { getSpeciality } from '../CareTeamCard/CareTeamUtils';
import { Constants, ReverseCoverageTypesCodes } from '../Constants';
import { ConstantsRoutes } from '../ConstantsRoutes';
import { phoneOnly } from '../ConstantsStyles';
import { SnackRole } from '../DataCard/DataCard.style';
import {
  HeaderContentWrapper,
  HeaderFlex,
} from '../DetailsPageHeader/DetailsPageHeader.styled';
import { ImageComponent } from '../ImageComponent';
import { Breadcrumb } from '../PSXHeader/Breadcrumb';
import { truncateLargeStrings } from '../Utils/truncateLargeStrings';
import { MapViewLocationsWrap } from './MapViewLocationsWrap/MapViewLocationsWrap';
import {
  CareTeamCardRow,
  HeaderContainer,
  HeaderPageWrapper,
  MapContainer,
  MapViewWrapper,
  ProviderInfoCard,
  ProviderInfoCardContainer,
} from './ProviderLocationMap.styled';

export const ProviderLocationMap = () => {
  const { t } = useTranslation();
  const imgRef = useRef<HTMLImageElement>(null);

  const { providerDetails } = useCareTeamStore(
    useShallow((state: CareTeamState) => state)
  );
  const locationsDetails = providerDetails?.providerLocations;
  const hasMultipleLocations = locationsDetails
    ? locationsDetails?.length > 1
    : false;
  const [mobileRouteView, setMobileRouteView] = useState<boolean>(false);
  const mobileScreen = useMediaQuery(phoneOnly);
  const intialIndex = getSelectedLocationIndex(locationsDetails, '');
  const { navigate } = useRouter();
  const [selectedAddressIndex, setSelectedAddressIndex] =
    useState<number>(intialIndex);
  const [currentIndex, setCurrentIndex] = useState<number>(intialIndex);

  const [breadcrumbSessionStorage, setBreadcrumbSessionStorage] =
    useSessionStorage<BreadcrumbSessionStorage>(
      Constants.STORAGE_KEYS.SESSION.BREADCRUMB_URLS,
      {}
    );

  useEffect(() => {
    breadcrumbSessionStorage[ConstantsRoutes.PROVIDER_DETAILS.key] =
      window.location.pathname.replace('/findcare', '') +
      window.location.search;
    setBreadcrumbSessionStorage(breadcrumbSessionStorage);
  }, []);

  const queryParams = new URLSearchParams(window.location.search);
  const updateLocation = queryParams.get('updateLocation') === 'true' || false;
  const location = useLocation();
  const {
    selectedLocation,
    providerId,
    imgSrc: providerImg,
  } = location.state ?? {};

  const parseBreadcrumbs = (urls) => {
    const breadcrumbs = [
      {
        title: updateLocation ? t('PROVIDER_LOCATION') : t('GET_DIRECTION'),
        href: '',
      },
    ];
    if (urls[ConstantsRoutes.CARE_TEAM.key]) {
      breadcrumbs.unshift({
        title: t('CARE_TEAM.CARE_TEAM_BREADCRUMB'),
        href: `${urls[ConstantsRoutes.CARE_TEAM.key]}`,
      });
    }
    return breadcrumbs;
  };
  const breadcrumbs = parseBreadcrumbs(breadcrumbSessionStorage);
  const stickyButtonRowCheck = (hasMultipleLocations, mobileScreen) =>
    hasMultipleLocations && !mobileScreen;
  const useStickyButtonRow = stickyButtonRowCheck(
    hasMultipleLocations,
    mobileScreen
  );

  const providerName = parseProviderName(
    providerDetails?.providerName || '',
    providerDetails?.providerType || ProviderType.PRACTITIONER,
    providerDetails?.primaryDegrees,
    getNameAttrs(providerDetails)
  );

  const handleGoBack = () => {
    navigate(-1);
  };

  const isGEHAV2 = shouldUseGehaV2Styling();

  const getCardRow = (provider) => {
    const providerSpecialityTitle = getSpecialityTitle(
      provider?.providerType,
      provider?.chipValue
        ? ReverseCoverageTypesCodes[provider?.chipValue.toUpperCase()]
        : ReverseCoverageTypesCodes.MEDICAL,
      getSpeciality(provider),
      t
    );
    return (
      <ProviderInfoCardContainer>
        {mobileScreen && (
          <Link
            data-auto-testid="back-to-locations-details"
            data-testid="back-to-locations-details"
            href="#"
            isStandardAnchor
            onClick={handleGoBack}
            style={{ marginRight: '8px' }}
          >
            <IconMaterial
              color="$uhcPrimary1"
              css={{
                width: '24px',
                height: '24px',
              }}
              icon="arrow_back"
              title="Back"
            />
          </Link>
        )}
        <ProviderInfoCard data-testid="provider-item" key={provider.providerId}>
          <Avatar size="48px" variant="solid">
            {ImageComponent({
              imgRef,
              providerType: provider.providerType,
              imgSrc: providerImg ?? provider.imageLocation,
              bhIcon: provider?.bhIcon,
              sizeToken: '48px',
              expandedProviderListView: false,
            })}
          </Avatar>
          <CareTeamCardRow>
            <SnackRole
              css={{ fontWeight: '700', fontSize: '14px' }}
              cssProps={{ isGEHAV2 }}
              data-auto-testid="provider-name"
              data-testid="provider-name"
            >
              {providerName}
            </SnackRole>
            <CareTeamProviderSpecialtyContainer>
              <Tooltip content={providerSpecialityTitle}>
                <Text
                  data-auto-testid="provider-speciality"
                  data-testid="provider-speciality"
                >
                  {truncateLargeStrings(providerSpecialityTitle, 20)}
                </Text>
              </Tooltip>
            </CareTeamProviderSpecialtyContainer>
          </CareTeamCardRow>
        </ProviderInfoCard>
      </ProviderInfoCardContainer>
    );
  };

  const [, setNavigateToDirections] = useState(Boolean(false));
  const handleUpdateLocation = () => {
    adobeLinkTrackEvent({
      name: 'save location',
      location: `body:${Constants.PROVIDER_DETAILS.VIEWONMAP}`,
      type: 'internal',
    });
    setSelectedAddressIndex(currentIndex);
    setNavigateToDirections(false);
  };

  const isSameLocationSelected = selectedAddressIndex === currentIndex;
  return (
    <React.Fragment>
      {!mobileRouteView && (
        <HeaderPageWrapper>
          <HeaderContainer>
            <HeaderContentWrapper>
              <HeaderFlex alignItems="center" justify="space-between">
                {!mobileScreen && (
                  <Breadcrumb
                    backgroundColor="$bgProviderDetailsColor"
                    breadcrumbs={breadcrumbs}
                    onBackButtonClick={handleGoBack}
                    showBreadcrumbAtMediumScreenSize={true}
                    showBreadcrumbAtSmallScreenSize={true}
                  />
                )}
              </HeaderFlex>
              {getCardRow(providerDetails)}
            </HeaderContentWrapper>
          </HeaderContainer>
        </HeaderPageWrapper>
      )}

      <MapViewWrapper>
        <MapContainer>
          <MapViewLocationsWrap
            currentIndex={currentIndex}
            directionsButtonClicked={true}
            handleGoBack={handleGoBack}
            handleUpdateLocation={handleUpdateLocation}
            hasMultipleLocations={hasMultipleLocations}
            isSameLocationSelected={isSameLocationSelected}
            mobileRouteView={mobileRouteView}
            mobileScreen={mobileScreen}
            providerDetails={providerDetails}
            providerId={providerId}
            providerSelectedLocation={selectedLocation || null}
            setCurrentIndex={setCurrentIndex}
            setMobileRouteView={setMobileRouteView}
            updateLocation={updateLocation}
            useStickyButtonRow={useStickyButtonRow}
          />
        </MapContainer>
      </MapViewWrapper>
    </React.Fragment>
  );
};
